import { useTranslation } from '@/lib/i18n';
import { labelOf, WEBHOOK_EVENT_TYPE_LABELS } from '@/lib/enumLabels';
import type { AdminPaymentWebhookLogEntry } from '@/lib/api-admin/types';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui';

export interface WebhookEventLogProps {
  events: ReadonlyArray<AdminPaymentWebhookLogEntry>;
}

const formatDateTime = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => `${n}`.padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

export const WebhookEventLog = ({ events }: WebhookEventLogProps) => {
  const { t } = useTranslation('payments');

  if (events.length === 0) {
    return (
      <p className="text-sm text-[color:var(--content-muted)]">
        {t('payments.detail.webhooksEmpty')}
      </p>
    );
  }

  return (
    <Table data-testid="payment-webhook-log">
      <TableHeader>
        <TableRow>
          <TableHead>{t('payments.detail.webhookReceivedAt')}</TableHead>
          <TableHead>{t('payments.detail.webhookEventType')}</TableHead>
          <TableHead>{t('payments.detail.webhookEventId')}</TableHead>
          <TableHead>{t('payments.detail.webhookProcessed')}</TableHead>
          <TableHead>{t('payments.detail.webhookSignature')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {events.map((evt) => (
          <TableRow key={evt.eventId} data-event-id={evt.eventId}>
            <TableCell className="tabular-nums text-[color:var(--content-default)]">
              {formatDateTime(evt.receivedAt)}
            </TableCell>
            <TableCell className="text-[color:var(--content-default)]">
              {labelOf(WEBHOOK_EVENT_TYPE_LABELS, evt.eventType)}
            </TableCell>
            <TableCell className="font-mono text-xs text-[color:var(--content-muted)]">
              {evt.eventId}
            </TableCell>
            <TableCell>
              <span
                className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                style={{
                  background: evt.processed ? 'var(--status-success-bg)' : 'var(--status-neutral-bg)',
                  color: evt.processed ? 'var(--status-success-fg)' : 'var(--status-neutral-fg)',
                }}
              >
                {evt.processed
                  ? t('payments.detail.webhookProcessedYes')
                  : t('payments.detail.webhookProcessedNo')}
              </span>
            </TableCell>
            <TableCell>
              <span
                className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                style={{
                  background: evt.signatureValid ? 'var(--status-success-bg)' : 'var(--status-danger-bg)',
                  color: evt.signatureValid ? 'var(--status-success-fg)' : 'var(--status-danger-fg)',
                }}
              >
                {evt.signatureValid
                  ? t('payments.detail.webhookSignatureValid')
                  : t('payments.detail.webhookSignatureInvalid')}
              </span>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
